import React from 'react';

import { BiometryKind } from 'components/BiometricPrototype/biometricGlyphs';
import {
  BiometricSurfaceHost,
  EnrolledConfirmation,
  EnrollmentNotCompletedCard,
  EnrollmentOfferCard,
  useSheetDismiss,
  useSheetEngaged
} from 'components/BiometricPrototype/BiometricSurfaces';

/**
 * Post-login biometrics enrollment prompt for the native shells (AC2).
 *
 * DESIGN PASS 2026-08-19 (Jason). Previously a hand-rolled MUI dialog: a 🔑
 * emoji, `bgcolor: #1a1a1a`, centred on screen at every size. It worked, but it
 * belonged to no design system — so the one moment we ask a player to change
 * how they sign in looked unrelated to the registration and passkey flows that
 * ask the same kind of question.
 *
 * It now renders the d4 surfaces (`components/BiometricPrototype`), the same
 * system as `RegistrationPrototype`, so biometric login reads as part of that
 * family. What that buys, concretely:
 *
 *   - **Bottom drawer on mobile, centred dialog on desktop** — decided by
 *     `BiometricSurfaceHost`, not by this component. A drawer is the platform
 *     norm for a dismissible contextual task on a phone; a centred card is what
 *     desktop expects and what the registration overlay already uses.
 *   - **Benefit bullets** rather than one line of prose, so the value is
 *     scannable at the moment of decision.
 *   - **A confirmation beat.** Enrolment used to end in silence; with the Face
 *     ID prompt now firing at enrolment (see `nativeBiometric.enrollBiometric`)
 *     the OS confirms the SCAN, but nothing confirmed that biometric sign-in is
 *     actually on for this account. Different message, so it needs its own.
 *
 * The props are unchanged apart from `enrolled` — the call site in App.tsx only
 * gains one line — so this stays easy to review against the version it replaces.
 *
 * ALL COPY DRAFT pending Legal/Compliance.
 */

/**
 * Runs the confirmation's timed exit through the host's animation rather than
 * unmounting under it. Renders nothing.
 */
function AutoDismiss({ after, onDone }: { after: number; onDone?: () => void }) {
  const dismiss = useSheetDismiss();
  const engaged = useSheetEngaged();
  React.useEffect(() => {
    // Hands off once the player has touched the sheet — the drawer is theirs to
    // close from that point, and a timer firing mid-drag would pull it out from
    // under their finger.
    if (engaged) return undefined;
    const timer = setTimeout(() => dismiss(onDone), after);
    return () => clearTimeout(timer);
  }, [after, dismiss, engaged, onDone]);
  return null;
}

/** The hook hands us a display label; the d4 glyphs need the sensor kind. */
function toBiometryKind(label: string): BiometryKind {
  const l = (label || '').toLowerCase();
  if (l.includes('face')) return 'faceid';
  if (l.includes('touch')) return 'touchid';
  return 'fingerprint';
}

export default function BiometricEnrollmentPrompt({
  open,
  biometryLabel,
  busy,
  enrolled,
  failed,
  onEnroll,
  onSkip,
  onRetry,
  onConfirmationDone
}: {
  open: boolean;
  biometryLabel: string;
  busy?: boolean;
  /** True for the brief confirmation beat after a successful enrolment. */
  enrolled?: boolean;
  /** True when the scan didn't complete — the soft landing, not an error. */
  failed?: boolean;
  onEnroll: () => void;
  onSkip: () => void;
  onRetry?: () => void;
  onConfirmationDone?: () => void;
}) {
  // The confirmation auto-dismisses (matching the registration flow's finale):
  // the player already acted, and iOS has already shown its own scan tick, so
  // making them dismiss a second surface would tax a decision they completed.
  // NOTE: the auto-dismiss is fired from inside the host (see AutoDismiss
  // below) so it runs the same slide-down + blur fade as every other exit.
  // Firing it from here would unmount the surface mid-animation.

  if (!open && !enrolled && !failed) return null;
  const biometry = toBiometryKind(biometryLabel);

  return (
    <BiometricSurfaceHost
      native
      // Draggable on EVERY card, including the confirmation. The first version
      // locked the confirmation because it auto-dismisses anyway — but a player
      // who has read it in half a second reaches for the grabber and finds a
      // drawer that ignores them, which is worse than no gesture at all: a
      // handle that does not respond teaches that none of them do (Jason,
      // on-device 2026-08-19). Auto-dismiss is a floor, not a lock.
      //
      // Mid-scan is the one exception. There the drawer is not the frontmost
      // surface — the OS prompt is — so dragging it is a gesture aimed at
      // something the player cannot see the consequences of.
      dismissible={!busy}
      onDismiss={busy ? undefined : enrolled || failed ? onConfirmationDone : onSkip}
      ariaLabel={
        enrolled
          ? 'Biometric sign-in enabled'
          : failed
          ? 'Biometric sign-in setup not completed'
          : 'Set up biometric sign-in'
      }>
      {enrolled ? (
        <>
          {/* 3250ms, up from 2600 (owner 2026-08-19). Long enough to read
              "Face ID is on" plus the line under it without hurrying; the
              grabber above is the exit for anyone who is already done. */}
          <AutoDismiss after={3250} onDone={onConfirmationDone} />
          <EnrolledConfirmation skin='hijack' biometry={biometry} onDone={onConfirmationDone} />
        </>
      ) : failed ? (
        <EnrollmentNotCompletedCard
          skin='hijack'
          biometry={biometry}
          busy={busy}
          onRetry={onRetry}
          onDismiss={onConfirmationDone}
        />
      ) : (
        <EnrollmentOfferCard
          skin='hijack'
          biometry={biometry}
          busy={busy}
          onEnroll={onEnroll}
          onSkip={onSkip}
        />
      )}
    </BiometricSurfaceHost>
  );
}
